import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Check, Share2, Mail, AlertTriangle, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { copyToClipboard } from "../../lib/clipboard";
import { buildMailto } from "../../lib/secureFiles/link";
import { intlLocaleFor } from "../../i18n";

// Lien complet affiché UNE seule fois (état React uniquement, jamais stocké ni envoyé à une API).
export const SecureFileResult = ({ result, onReset }) => {
  const { t, i18n } = useTranslation("files");
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const date = new Intl.DateTimeFormat(intlLocaleFor(i18n.resolvedLanguage), { dateStyle: "medium", timeStyle: "short" })
    .format(new Date(result.expiresAt));

  const copy = async () => {
    if (await copyToClipboard(result.link)) {
      setCopied(true);
      toast.success(t("result.copied"));
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error(t("result.copyFailed"));
    }
  };
  const share = () => navigator.share({ title: t("mail.subject"), text: t("mail.subject"), url: result.link }).catch(() => {});
  const mail = () => {
    window.location.assign(buildMailto({ to: result.recipient, subject: t("mail.subject"), body: t("mail.body", { link: result.link }) }));
  };

  return (
    <div data-testid="sf-result" className="space-y-4">
      <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" />
        <h2 className="font-heading text-lg font-semibold text-slate-900">{t("result.title")}</h2></div>
      <div data-testid="sf-result-warning" className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{t("result.warning")}</span>
      </div>
      <div>
        <label htmlFor="sf-link" className="text-xs font-medium text-slate-500 mb-1 block">{t("result.linkLabel")}</label>
        <textarea id="sf-link" data-testid="sf-result-link" readOnly value={result.link} rows={3} onFocus={(e) => e.target.select()}
          className="w-full resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800 break-all" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Button data-testid="sf-copy-btn" onClick={copy} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}{copied ? t("result.copied") : t("result.copy")}
        </Button>
        {canShare && <Button data-testid="sf-share-btn" variant="outline" onClick={share}><Share2 className="w-4 h-4 mr-2" />{t("result.share")}</Button>}
        <Button data-testid="sf-mailto-btn" variant="outline" onClick={mail}><Mail className="w-4 h-4 mr-2" />{t("result.prepareEmail")}</Button>
      </div>
      <p data-testid="sf-result-expires" className="text-xs text-slate-500">{t("result.oneTime")} {t("result.expiresAt", { date })}</p>
      <Button data-testid="sf-another-btn" variant="ghost" onClick={onReset} className="text-slate-600"><RotateCcw className="w-4 h-4 mr-2" />{t("result.another")}</Button>
    </div>
  );
};

export default SecureFileResult;
